import { motion, AnimatePresence } from "framer-motion";
import { WifiOff, RefreshCw, CheckCircle, Upload } from "lucide-react";

/**
 * ConnectionBanner — Offline / Reconnecting status bar.
 *
 * Props:
 *   status    — 'connected' | 'reconnecting' | 'connecting' | 'offline'
 *   queueSize — number of actions buffered offline (0 = hide sync bar)
 */
export function ConnectionBanner({ status, queueSize = 0 }) {
  const isOffline = status === "offline" || status === "reconnecting" || status === "connecting";
  const isSyncing = status === "connected" && queueSize > 0;

  return (
    <AnimatePresence>
      {(isOffline || isSyncing) && (
        <motion.div
          data-testid="connection-banner"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{ overflow: "hidden" }}
        >
          <div
            className="flex items-center justify-between px-6 py-2.5 text-sm font-medium"
            style={{
              background: isSyncing
                ? "rgba(0,255,65,0.08)"
                : status === "offline"
                ? "rgba(239,68,68,0.12)"
                : "rgba(245,158,11,0.1)",
              borderBottom: isSyncing
                ? "1px solid rgba(0,255,65,0.2)"
                : status === "offline"
                ? "1px solid rgba(239,68,68,0.25)"
                : "1px solid rgba(245,158,11,0.2)",
            }}
          >
            <div className="flex items-center gap-2.5">
              {isSyncing ? (
                <>
                  <Upload className="w-4 h-4 text-[#00FF41] animate-pulse" />
                  <span style={{ color: "#00FF41" }}>
                    Syncing {queueSize} offline action{queueSize !== 1 ? "s" : ""}
                    {" "}— connection restored
                  </span>
                </>
              ) : status === "offline" ? (
                <>
                  <WifiOff className="w-4 h-4 text-red-400" />
                  <span className="text-red-300">
                    Offline — any completed tasks are saved locally
                  </span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 text-amber-400 animate-spin" />
                  <span className="text-amber-300">
                    Reconnecting to live updates
                    {queueSize > 0 && ` — ${queueSize} action${queueSize !== 1 ? "s" : ""} queued`}
                  </span>
                </>
              )}
            </div>

            {/* Queue size pill */}
            {queueSize > 0 && (
              <span
                className="text-xs font-mono px-2 py-0.5 rounded-full"
                style={{
                  background: isSyncing ? "rgba(0,255,65,0.15)" : "rgba(245,158,11,0.15)",
                  color: isSyncing ? "#00FF41" : "#f59e0b",
                  border: `1px solid ${isSyncing ? "rgba(0,255,65,0.3)" : "rgba(245,158,11,0.3)"}`,
                }}
              >
                {queueSize} queued
              </span>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * WsIndicator — Small live-status dot for the Command Centre header.
 */
export function WsIndicator({ status }) {
  const configs = {
    connected:    { color: "#00FF41", label: "Live",         pulse: true },
    reconnecting: { color: "#f59e0b", label: "Reconnecting", pulse: true },
    connecting:   { color: "#f59e0b", label: "Connecting",   pulse: true },
    offline:      { color: "#ef4444", label: "Offline",      pulse: false },
  };

  const { color, label, pulse } = configs[status] || configs.offline;

  return (
    <div
      data-testid="ws-indicator"
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
      style={{
        background: color + "14",
        border: `1px solid ${color}33`,
        color,
      }}
    >
      <span className="relative flex h-2 w-2">
        {pulse && (
          <span
            className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
            style={{ background: color }}
          />
        )}
        <span
          className="relative inline-flex rounded-full h-2 w-2"
          style={{ background: color }}
        />
      </span>
      {label}
    </div>
  );
}
